import {useRef, useState, useContext} from 'react';
import classNames from 'classnames';
import {isNumber} from 'lodash-es';

import Context, {DragContext} from './Context';

import styles from './index.module.scss';

const threshold = 4;
enum Placement {
    Up = 'top',
    Down = 'down',
}

let dragMode = false;
let dragShadowEl: HTMLElement | null = null;
const dropData: {
    dropTarget?: HTMLElement | null;
    dropPlacement?: Placement | null;
    dropIndex?: number | null;
} = {};

interface IProps {
    index: number;
    onDrop?: (index: number) => void;
    disabled?: boolean;
    dragShadowWrapClassName?: string;
}

const Drag: React.FC<IProps> = ({children, index, onDrop, disabled = false, dragShadowWrapClassName = ''}) => {
    const {setDragMode} = useContext(DragContext);
    const ref = useRef<HTMLDivElement | null>(null);
    const [isDragTarget, setIsDragTarget] = useState(false);
    const [placement, setPlacement] = useState<Placement | null>(null);

    // 拖拽
    const onMouseDown = (e: React.MouseEvent) => {
        // 不是左键点击不应该触发拖拽
        if (disabled || e.button !== 0) {
            return;
        }
        const initX = e.pageX;
        const initY = e.pageY;

        function onMouseMove(e: MouseEvent) {
            const distanceX = Math.abs(e.pageX - initX);
            const distanceY = Math.abs(e.pageY - initY);
            if (!dragMode && distanceX > threshold || distanceY > threshold) {
                dragMode = true;
                setDragMode(dragMode);
                setIsDragTarget(true);
            }

            const target = ref.current;
            if (dragMode && target) {
                dragShadowEl = dragShadowEl || target.cloneNode(true) as HTMLElement;
                dragShadowEl.className = classNames(styles.dragShadowWrap, dragShadowWrapClassName);
                dragShadowEl.style.left = `${e.pageX + 3}px`;
                dragShadowEl.style.top = `${e.pageY + 3}px`;
                document.body.append(dragShadowEl);
            }
        }
        function onMouseUp() {
            if (dropData.dropTarget && dropData.dropPlacement && isNumber(dropData.dropIndex)) {
                onDrop && onDrop(dropData.dropIndex);
            }

            dragMode = false;
            setDragMode(dragMode);
            setIsDragTarget(false);
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
            if (dragShadowEl && dragShadowEl.parentElement) {
                dragShadowEl.parentElement.removeChild(dragShadowEl);
                dragShadowEl = null;
            }
        }

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
    };

    // 放置
    const onMouseEnter = () => {
        if (dragMode) {
            const target = ref.current;
            if (target) {
                const rect = target.getBoundingClientRect();

                const onMouseMove = (e: MouseEvent) => {
                    const offsetY = e.pageY - rect.top;
                    const placement = offsetY > rect.height / 2 ? Placement.Down : Placement.Up;
                    setPlacement(placement);
                    dropData.dropTarget = target;
                    dropData.dropPlacement = placement;
                    dropData.dropIndex = index + (placement === Placement.Down ? 1 : 0);
                };
                const onMouseLeave = () => {
                    setPlacement(null);
                    dropData.dropTarget = null;
                    dropData.dropPlacement = null;
                    dropData.dropIndex = null;
                    target.removeEventListener('mousemove', onMouseMove);
                    target.removeEventListener('mouseleave', onMouseLeave);
                };
                target.addEventListener('mousemove', onMouseMove);
                target.addEventListener('mouseleave', onMouseLeave);
            }
        }
    };

    return (
        <div
            ref={ref}
            className={classNames(styles.drag, {
                [styles.disabled]: isDragTarget,
                [styles.mask]: dragMode && placement,
                [styles.up]: dragMode && placement === Placement.Up,
                [styles.down]: dragMode && placement === Placement.Down,
            })}
            onMouseDown={onMouseDown}
            onMouseEnter={onMouseEnter}
        >
            {children}
        </div>
    );
};

export default Object.assign(Drag, {Context});
